import { useEffect, useState } from 'react';
import { supabase } from '@/src/lib/supabase';
import type { PendingAction } from '@/src/lib/types';
import type { DbAiPendingAction, DbActionStatus } from '@/src/lib/db-types';
import { prettifyActionType } from '@/src/lib/format';
import { log } from '@/src/lib/logger';

function mapStatus(s: DbActionStatus): PendingAction['status'] {
  switch (s) {
    case 'pending':
      return 'pending';
    case 'approved':
    case 'executing':
    case 'executed':
      return 'accepted';
    case 'rejected':
    case 'failed':
    case 'expired':
      return 'rejected';
    default:
      return 'pending';
  }
}

function mapRow(row: DbAiPendingAction): PendingAction & { priority?: string } {
  return {
    id: row.id,
    type: prettifyActionType(row.action_type),
    reason: row.reasoning ?? '',
    affectedSystems: (row.affected_systems ?? []).map((s) => s.toUpperCase()),
    status: mapStatus(row.status),
    priority: (row.priority as string | undefined) ?? 'normal',
  };
}

export function usePendingActions(venueId: string | null): {
  actions: PendingAction[];
  loading: boolean;
  error: string | null;
} {
  const [actions, setActions] = useState<PendingAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!venueId) {
      setActions([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);

    (async () => {
      const { data, error: err } = await supabase
        .from('ai_pending_actions')
        .select(
          'id, venue_id, action_type, reasoning, affected_systems, status, priority, created_at',
        )
        .eq('venue_id', venueId)
        .in('status', ['pending', 'executing'])
        .order('created_at', { ascending: false })
        .limit(50);
      if (cancelled) return;
      if (err) {
        log.error('usePendingActions: load failed', { code: err.code });
        setError(err.message);
        setLoading(false);
        return;
      }
      setActions((data ?? []).map((r) => mapRow(r as DbAiPendingAction)));
      setLoading(false);
    })();

    const channel = supabase
      .channel(`pending-actions:${venueId}:${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'ai_pending_actions', filter: `venue_id=eq.${venueId}` },
        (payload) => {
          setActions((prev) => {
            if (payload.eventType === 'INSERT') {
              const row = payload.new as DbAiPendingAction;
              if (!['pending', 'executing'].includes(row.status)) return prev;
              return [mapRow(row), ...prev.filter((a) => a.id !== row.id)];
            }
            if (payload.eventType === 'UPDATE') {
              const row = payload.new as DbAiPendingAction;
              if (!['pending', 'executing'].includes(row.status)) {
                return prev.filter((a) => a.id !== row.id);
              }
              return prev.map((a) => (a.id === row.id ? mapRow(row) : a));
            }
            if (payload.eventType === 'DELETE') {
              const row = payload.old as DbAiPendingAction;
              return prev.filter((a) => a.id !== row.id);
            }
            return prev;
          });
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [venueId]);

  return { actions, loading, error };
}
